import { UseFormReturn, FieldValues, Path } from 'react-hook-form'
import { OrderCustomAnswersSlice } from 'modules/order/state/custom-answers/custom-answers-types'
import { OrderContactAnswersSlice } from 'modules/order/state/contact-answers/contact-answers-types'
import { OrderContactSlice } from 'modules/order/state/contact/contact-types'
import { OrderAddressValue } from 'modules/order/state/address/address-types'

/**
 * Single Step Form Orchestrator
 *
 * Global form management for Single Step flow.
 * Provides functions to register, unregister, and validate multiple forms.
 */

// Simple form registry for Single Step only
interface FormEntry<T extends FieldValues = FieldValues> {
  name: string
  formRef: UseFormReturn<T>
  validator: (formRef: UseFormReturn<T>) => Promise<{ success: boolean; data?: T; error?: string }>
}

// Interface for registerForm parameters
interface RegisterFormParams<T extends FieldValues = FieldValues> {
  name: string
  formRef: UseFormReturn<T>
  validator?: FormEntry<T>['validator']
  errorMessage?: string
}

// Interface for unregisterForm parameters
interface UnregisterFormParams {
  name: string
}

// Interface for validateForms parameters
interface ValidateFormsParams {
  formNames?: string[]
}

// Interface for validation results
interface ValidationResult {
  formName: string
  success: boolean
  data?: FieldValues
  error?: string
}

// Interface for order state used in saveFormsData
interface SaveFormsDataOrderState {
  customAnswers: {
    setValue: (value: Record<string, string>) => void
    value: OrderCustomAnswersSlice
  }
  contactAnswers: {
    setValue: (value: OrderContactAnswersSlice) => void
    value: OrderContactAnswersSlice
  }
  contact: { setValue: (value: OrderContactSlice) => void; value: OrderContactSlice }
  address: { setValue: (value: OrderAddressValue) => void; value: OrderAddressValue }
}

// Simple in-memory registry (only for Single Step)
class SingleStepFormRegistry {
  private forms = new Map<string, FormEntry>()

  register<T extends FieldValues>(
    name: string,
    formRef: UseFormReturn<T>,
    validator: FormEntry<T>['validator'],
  ) {
    this.forms.set(name, { name, formRef, validator } as FormEntry)
  }

  unregister(name: string) {
    this.forms.delete(name)
  }

  getActiveForms(): FormEntry[] {
    return Array.from(this.forms.values()).filter((form) => form.formRef)
  }

  clear() {
    this.forms.clear()
  }
}

// Single instance for the Single Step
const singleStepRegistry = new SingleStepFormRegistry()

// Default validator factory for common validation logic
const createDefaultValidator = <T extends FieldValues>(
  formRef: UseFormReturn<T>,
  errorMessage: string,
): FormEntry<T>['validator'] => {
  return async () => {
    return new Promise<{ success: boolean; data?: T; error?: string }>((resolve) => {
      formRef.handleSubmit(
        (data) => resolve({ success: true, data }),
        (errors) => {
          const firstErrorField = Object.keys(errors)[0]

          if (firstErrorField) {
            formRef.setFocus(firstErrorField as Path<T>)
          }

          resolve({
            success: false,
            error: errorMessage,
          })
        },
      )()
    })
  }
}

// ===== FORM ORCHESTRATOR FUNCTIONS =====

/**
 * Register a form with the global form orchestrator
 */
export function registerForm<T extends FieldValues>(params: RegisterFormParams<T>): void {
  const { name, formRef, validator, errorMessage } = params

  const finalValidator =
    validator ||
    createDefaultValidator(formRef, errorMessage || `Please fill in all required ${name} fields`)

  singleStepRegistry.register(name, formRef, finalValidator)
}

/**
 * Unregister a form from the global form orchestrator
 */
export function unregisterForm(params: UnregisterFormParams): void {
  const { name } = params
  singleStepRegistry.unregister(name)
}

/**
 * Validate specific forms by name, or all forms if no names provided
 */
export async function validateForms(params: ValidateFormsParams = {}) {
  const { formNames } = params
  const activeForms = singleStepRegistry.getActiveForms()

  // If specific form names provided, filter to only those forms
  const formsToValidate = formNames
    ? activeForms.filter((form) => formNames.includes(form.name))
    : activeForms

  if (formsToValidate.length === 0) {
    return { success: true, results: [] }
  }

  // Validate forms in parallel
  const validationPromises = formsToValidate.map(async (form) => {
    try {
      const result = await form.validator(form.formRef)

      return { formName: form.name, ...result }
    } catch (error) {
      return {
        formName: form.name,
        success: false,
        error: `Validation failed for ${form.name}`,
      }
    }
  })

  const results = await Promise.allSettled(validationPromises)

  // Process results
  const validationResults = results.map((result) =>
    result.status === 'fulfilled'
      ? result.value
      : {
          formName: 'unknown',
          success: false,
          error: 'Validation promise rejected',
        },
  )

  const allValid = validationResults.every((result) => result.success)
  const firstError = validationResults.find((result) => !result.success)

  return {
    success: allValid,
    results: validationResults,
    error: firstError?.error,
  }
}

/**
 * Validate all registered forms
 */
export async function validateAllForms() {
  return validateForms()
}

/**
 * Get available form names (useful for debugging)
 */
export function getAvailableFormNames(): string[] {
  const activeForms = singleStepRegistry.getActiveForms()

  return activeForms.map((form) => form.name)
}

/**
 * Universal function to save all form data to Redux store
 * This provides a consistent pattern across all payment methods
 *
 * @param validationResults - Results from validateForms()
 * @param order - Order state containing all form data stores
 */
export function saveFormsData(
  validationResults: ValidationResult[],
  order: SaveFormsDataOrderState,
): void {
  for (const result of validationResults) {
    if (!result.success || !result.data) {
      continue // Skip failed validations or empty data
    }

    switch (result.formName) {
      case 'customQuestions':
      case 'requiredCustomQuestions':
      case 'optionalCustomQuestions':
        order.customAnswers.setValue(result.data as Record<string, string>)
        break

      case 'contactQuestions':
      case 'requiredContactQuestions':
      case 'optionalContactQuestions':
        saveAddressData(result.data, order)
        order.contactAnswers.setValue(result.data as OrderContactAnswersSlice)
        break

      case 'creditCardDonorInfo':
      case 'donorInfo':
      case 'ach':
        saveDonorInfoData(result.data, order)
        break

      case 'donationOptions':
      case 'anonymousName':
      case 'hideAmount':
      case 'optIn':
      case 'optInSms':
      case 'orgDonation':
        saveDonationOptionsData(result.data, order)
        break

      // Future forms can be easily added here:
      // case 'newFormType':
      //   saveNewFormData(result.data, order)
      //   break

      default:
        // Skip unknown form types
        break
    }
  }
}

/**
 * Helper function to save donor info data (contact and address fields)
 */
function saveDonorInfoData(data: FieldValues, order: SaveFormsDataOrderState): void {
  // Extract contact fields (email, firstName, lastName)
  const contactData: Partial<OrderContactSlice> = {}
  if (data.email !== undefined) contactData.email = data.email as string
  if (data.firstName !== undefined) contactData.firstName = data.firstName as string
  if (data.lastName !== undefined) contactData.lastName = data.lastName as string

  if (Object.keys(contactData).length > 0) {
    order.contact.setValue({ ...order.contact.value, ...contactData })
  }

  saveAddressData(data, order)
}

function saveDonationOptionsData(data: FieldValues, order: SaveFormsDataOrderState): void {
  const contactData: Partial<OrderContactSlice> = {}
  if (data.isCompanyCard !== undefined) contactData.isCompanyCard = data.isCompanyCard as boolean
  if (data.cardCompanyName !== undefined)
    contactData.cardCompanyName = data.cardCompanyName as string
  if (data.isNameAnonymous !== undefined)
    contactData.isNameAnonymous = data.isNameAnonymous as boolean
  if (data.isAmountAnonymous !== undefined)
    contactData.isAmountAnonymous = data.isAmountAnonymous as boolean
  if (data.optIn !== undefined) contactData.optIn = data.optIn as boolean

  if (Object.keys(contactData).length > 0) {
    order.contact.setValue({ ...order.contact.value, ...contactData })
  }
}

function saveAddressData(data: FieldValues, order: SaveFormsDataOrderState): void {
  const addressData: Partial<OrderAddressValue> = {}

  if (data.country !== undefined) addressData.country = data.country as string
  if (data.zipCode !== undefined) addressData.zipCode = data.zipCode as string
  if (data.state !== undefined) addressData.state = data.state as string
  if (data.city !== undefined) addressData.city = data.city as string
  if (data.addressLine1 !== undefined) addressData.line1 = data.addressLine1 as string
  if (data.addressLine2 !== undefined) addressData.line2 = data.addressLine2 as string

  if (Object.keys(addressData).length > 0) {
    order.address.setValue({ ...order.address.value, ...addressData })
  }
}
